import React, { useState, useEffect } from 'react';
import axiosInstance from '../../services/axiosInstance';
import { 
    Search, 
    Filter, 
    ChevronRight, 
    Activity, 
    Clock, 
    User, 
    ArrowLeft, 
    RefreshCcw,
    AlertCircle,
    CheckCircle2,
    XCircle,
    Building2
} from 'lucide-react';
import ExecutionDetail from '../../components/ExecutionDetail';
import { formatTimestamp, formatDuration, compareDates } from '../../utils/dateUtils';
import './styles.scss';

const InstitutionalExecutions = () => {
    const [executions, setExecutions] = useState([]);
    const [totalExecutions, setTotalExecutions] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedExecution, setSelectedExecution] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('ALL');

    const [institutionMeta, setInstitutionMeta] = useState(null);

    const fetchExecutions = async () => {
        setLoading(true);
        setError(null);
        try {
            // 1. Fetch user's institution metadata first
            const metaResponse = await axiosInstance.get('/api/advising/my-institution');
            setInstitutionMeta(metaResponse.data);
            let { shortName } = metaResponse.data;
            
            if (!shortName) {
                shortName = localStorage.getItem('preferred_institution');
                if (shortName) {
                    setInstitutionMeta({
                        shortName,
                        fullName: localStorage.getItem('preferred_institution_name') || shortName
                    });
                }
            }
            
            if (!shortName) {
                setError('Could not resolve your institutional context.');
                return;
            }

            // 2. Fetch executions using the shortName
            const response = await axiosInstance.get(`/api/advising/executions/institution?institution=${encodeURIComponent(shortName)}`);
            // The API returns PaginatedExecutionsDTO { executions: [], total: X, limit: Y }
            const data = response.data.executions || [];
            const total = response.data.total || 0;
            
            // Sort by executedAt desc
            const sorted = data.sort((a, b) => compareDates(a.executedAt, b.executedAt));
            setExecutions(sorted);
            setTotalExecutions(total);
        } catch (err) {
            console.error('Failed to fetch institutional executions:', err);
            setError('Failed to load institutional workflow history. Please check your permissions.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchExecutions();
    }, []);

    const filteredExecutions = executions.filter(exec => {
        const matchesSearch = 
            exec.agentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            exec.executedBy?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            exec.agentExecutionId?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesStatus = filterStatus === 'ALL' || exec.status === filterStatus;
        
        return matchesSearch && matchesStatus;
    });

    const getStatusIcon = (status) => {
        switch (status) {
            case 'SUCCESS': return <CheckCircle2 className="status-icon completed" size={16} />;
            case 'FAILED': return <XCircle className="status-icon failed" size={16} />;
            case 'CANCELLED': return <AlertCircle className="status-icon cancelled" size={16} />;
            default: return <Activity className="status-icon active" size={16} />;
        }
    };


    if (selectedExecution) {
        return (
            <div className="executions-detail-page">
                <header className="page-header">
                    <button className="back-btn" onClick={() => setSelectedExecution(null)}>
                        <ArrowLeft size={20} />
                        <span>Back to Monitor</span>
                    </button>
                    <div className="header-info">
                        <div className="detail-label">INSTITUTIONAL MONITORING</div>
                        <h1>Execution Details</h1>
                        <span className="execution-id">{selectedExecution.agentExecutionId}</span>
                    </div>
                </header>
                <main className="detail-content">
                    <ExecutionDetail executionData={selectedExecution} />
                </main>
            </div>
        );
    }

    return (
        <div className="institutional-monitor-container">
            <header className="page-header-premium">
                <div className="hero-section">
                    <Building2 className="header-icon-large" />
                    <h1>
                        {institutionMeta?.fullName || executions[0]?.institution || 'Institutional'} <br />
                        <span className="text-gradient">Diagnostic Monitor</span>
                        <span className="total-badge">{totalExecutions} total</span>
                    </h1>
                    <p className="subtitle">
                        Review AI advising activity specifically for your institution. Monitor diagnostic accuracy and student engagement telemetry.
                    </p>
                </div>
                <div className="refresh-action">
                    <button className="refresh-btn" onClick={fetchExecutions} disabled={loading}>
                        <RefreshCcw size={18} className={loading ? 'spinning' : ''} />
                        <span>Sync History</span>
                    </button>
                </div>
            </header>

            <div className="controls-bar">
                <div className="search-wrapper">
                    <Search size={18} />
                    <input 
                        type="text" 
                        placeholder="Search diagnostics..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="filter-wrapper">
                    <Filter size={18} />
                    <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                        <option value="ALL">All Statuses</option>
                        <option value="SUCCESS">Success</option>
                        <option value="FAILED">Failed</option>
                        <option value="CANCELLED">Cancelled</option>
                        <option value="IN_PROGRESS">In Progress</option>
                    </select>
                </div>
            </div>

            {error && (
                <div className="error-state">
                    <AlertCircle size={40} />
                    <p>{error}</p>
                    <button onClick={fetchExecutions}>Try Again</button>
                </div>
            )}

            {loading && !executions.length ? (
                <div className="loading-state">
                    <div className="loader"></div>
                    <p>Fetching institutional monitor logs...</p>
                </div>
            ) : (
                <div className="executions-table-wrapper">
                    <table className="executions-table">
                        <thead>
                            <tr>
                                <th>Diagnostic Task</th>
                                <th>Institution</th>
                                <th>Advisor / Student</th>
                                <th>Status</th>
                                <th>Duration</th>
                                <th>Executed At</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredExecutions.map(exec => (
                                <tr key={exec._id?.$oid || exec.agentExecutionId} onClick={() => setSelectedExecution(exec)}>
                                    <td className="agent-col">
                                        <div className="agent-info">
                                            <span className="agent-name">
                                                {((exec.agentTaskName && exec.agentTaskName !== 'N/A') ? exec.agentTaskName : null) || 
                                                 ((exec.taskName && exec.taskName !== 'N/A') ? exec.taskName : null) || 
                                                 exec.agentName || 
                                                 'Advising Diagnosis'}
                                            </span>
                                            <span className="task-id">
                                                ID: {exec.agentExecutionId?.slice(0, 8) || exec.id?.slice(0, 8)}...
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="institution-info">
                                            <span className="institution-badge">
                                                {exec.institution || 
                                                 exec.resolvedInstitution ||
                                                 exec.request?.institution || 
                                                 exec.request?.query?.params?.institution || 
                                                 exec.inputData?.institution || 
                                                 'N/A'}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="user-info">
                                            <User size={14} />
                                            <span>{exec.executedBy || 'Guest User'}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className={`status-badge ${exec.status?.toLowerCase()}`}>
                                            {getStatusIcon(exec.status)}
                                            <span>{exec.status}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="duration-info">
                                            <Clock size={14} />
                                            <span>{formatDuration(exec.durationMs)}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="timestamp">
                                            {formatTimestamp(exec.executedAt, { 
                                                month: 'short', 
                                                day: 'numeric', 
                                                hour: '2-digit', 
                                                minute: '2-digit' 
                                            })}
                                        </span>
                                    </td>
                                    <td className="actions-col">
                                        <ChevronRight size={18} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {!filteredExecutions.length && (
                        <div className="empty-state">
                            <Activity size={40} />
                            <p>No institutional executions found</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default InstitutionalExecutions;
